import styled, { css } from 'styled-components';
import { unthemed } from 'tokens';
import { ThumbnailSize } from './index';

// Preview bar colors are locked to dark-bg / light-text regardless of theme
// (unthemed.gray[*] equals lightTheme values, so they stay dark in dark mode).
// Same family as Island/TopRight's dark button.
const PREVIEW_BAR_BG = unthemed.gray[1000];
const PREVIEW_BAR_BG_HOVER = unthemed.gray[900];
const PREVIEW_BAR_BG_ACTIVE = unthemed.gray[800];

export const PREVIEW_BAR_ICON_COLOR = '#FFFFFF';
export const PREVIEW_BAR_ICON_COLOR_DISABLED = 'rgba(255, 255, 255, 0.4)';

const getDimension = (size?: ThumbnailSize) => {
	switch (size) {
		case ThumbnailSize.RIGHT_PANEL:
			return `${ThumbnailSize.RIGHT_PANEL}px`;
		case ThumbnailSize.MODAL:
			return `${ThumbnailSize.MODAL}px`;
		case ThumbnailSize.CHAT:
			return `${ThumbnailSize.CHAT}px`;
		default:
			return `${ThumbnailSize.RIGHT_PANEL}px`;
	}
};

interface BaseProps {
	size?: ThumbnailSize;
	$split?: boolean;
}
export const Base = styled.div<BaseProps>`
	position: relative;
	width: ${(props) => getDimension(props.size)};
	height: ${(props) => getDimension(props.size)};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	${({ $split, theme }) =>
		$split &&
		css`
			flex-direction: column;
			align-items: stretch;
			border-radius: 12px;
			border: 1px solid ${theme.gray[200]};
			overflow: hidden;
		`}
`;

export const MediaArea = styled.div<{ $split?: boolean }>`
	position: relative;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;

	${({ $split }) =>
		$split &&
		css`
			flex: 1;
			min-height: 0;
		`}
`;

export const Icon = styled.div`
	position: absolute;
	// filter out mouse events on the icon, so that all clicks go to the base
	// otherwise Icon will block clicks on Base, and we won't detect when the user clicks the button
	pointer-events: none;
	top: -2px;
	right: -2px;
	pointer-events: all;
`;

interface MediaProps {
	size?: ThumbnailSize;
	$split?: boolean;
}

interface ImageProps extends MediaProps {}
export const Image = styled.img<ImageProps>`
	width: ${(props) => (props.$split ? '100%' : getDimension(props.size))};
	height: ${(props) => (props.$split ? '100%' : getDimension(props.size))};
	object-fit: cover;
	object-position: center;
	border-radius: ${(props) => (props.$split ? '0' : '12px')};
	${(props) =>
		!props.$split &&
		css`
			border: 1px solid rgba(255, 255, 255, 0.25);
		`}

	&:hover {
		filter: brightness(0.8);
	}
`;

interface LoadingProps extends MediaProps {}
export const Loading = styled.div<LoadingProps>`
	width: ${(props) => (props.$split ? '100%' : getDimension(props.size))};
	height: ${(props) => (props.$split ? '100%' : getDimension(props.size))};
	object-fit: cover;
	object-position: center;
	border-radius: ${(props) => (props.$split ? '0' : '12px')};
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${unthemed.gray[900]};
`;

interface ErrorProps extends MediaProps {}
export const Error = styled.div<ErrorProps>`
	width: ${(props) => (props.$split ? '100%' : getDimension(props.size))};
	height: ${(props) => (props.$split ? '100%' : getDimension(props.size))};
	object-fit: cover;
	object-position: center;
	border-radius: ${(props) => (props.$split ? '0' : '12px')};
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${({ theme }) => theme.gray[100]};
`;

export const PreviewBar = styled.div<{ $disabled?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 100%;
	height: 22px;
	background-color: ${PREVIEW_BAR_BG};
	opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
	transition: background-color 80ms ease;

	&:hover {
		background-color: ${({ $disabled }) => ($disabled ? PREVIEW_BAR_BG : PREVIEW_BAR_BG_HOVER)};
	}
	&:active {
		background-color: ${({ $disabled }) => ($disabled ? PREVIEW_BAR_BG : PREVIEW_BAR_BG_ACTIVE)};
	}
`;
